import 'moment/locale/cs';
import 'moment/locale/da';
import 'moment/locale/de';
import 'moment/locale/en-gb';
import 'moment/locale/et';
import 'moment/locale/fi';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/lt';
import 'moment/locale/lv';
import 'moment/locale/nb';
import 'moment/locale/nl';
import 'moment/locale/pl';
import 'moment/locale/ru';
import 'moment/locale/sv';
import 'moment/locale/uk';
import 'moment/locale/zh-cn';

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';

import 'moment-duration-format';

import { getStored } from '~/services/LegacyServices';

// Import all translation files statically so they can be bundled
import csError from './error-locales/cs.json';
import daError from './error-locales/da.json';
import deError from './error-locales/de.json';
import enError from './error-locales/en.json';
import etError from './error-locales/et.json';
import fiError from './error-locales/fi.json';
import fmError from './error-locales/fm.json';
import frError from './error-locales/fr.json';
import itError from './error-locales/it.json';
import ltError from './error-locales/lt.json';
import lvError from './error-locales/lv.json';
import nlError from './error-locales/nl.json';
import noError from './error-locales/no.json';
import plError from './error-locales/pl.json';
import ruError from './error-locales/ru.json';
import svError from './error-locales/sv.json';
import ukError from './error-locales/uk.json';
import zhError from './error-locales/zh.json';
import csFormio from './formio-locales/cs.json';
import daFormio from './formio-locales/da.json';
import deFormio from './formio-locales/de.json';
import enFormio from './formio-locales/en.json';
import etFormio from './formio-locales/et.json';
import fiFormio from './formio-locales/fi.json';
import fmFormio from './formio-locales/fm.json';
import frFormio from './formio-locales/fr.json';
import itFormio from './formio-locales/it.json';
import ltFormio from './formio-locales/lt.json';
import lvFormio from './formio-locales/lv.json';
import nlFormio from './formio-locales/nl.json';
import noFormio from './formio-locales/no.json';
import plFormio from './formio-locales/pl.json';
import svFormio from './formio-locales/sv.json';
import csLegacy from './legacy-locales/cs.json';
import daLegacy from './legacy-locales/da.json';
import deLegacy from './legacy-locales/de.json';
import enLegacy from './legacy-locales/en.json';
import etLegacy from './legacy-locales/et.json';
import fiLegacy from './legacy-locales/fi.json';
import fmLegacy from './legacy-locales/fm.json';
import frLegacy from './legacy-locales/fr.json';
import itLegacy from './legacy-locales/it.json';
import ltLegacy from './legacy-locales/lt.json';
import lvLegacy from './legacy-locales/lv.json';
import nlLegacy from './legacy-locales/nl.json';
import noLegacy from './legacy-locales/no.json';
import plLegacy from './legacy-locales/pl.json';
import ruLegacy from './legacy-locales/ru.json';
import svLegacy from './legacy-locales/sv.json';
import ukLegacy from './legacy-locales/uk.json';
import zhLegacy from './legacy-locales/zh.json';
import csLocale from './locales/cs.json';
import daLocale from './locales/da.json';
import deLocale from './locales/de.json';
import enLocale from './locales/en.json';
import etLocale from './locales/et.json';
import fiLocale from './locales/fi.json';
import fmLocale from './locales/fm.json';
import frLocale from './locales/fr.json';
import itLocale from './locales/it.json';
import ltLocale from './locales/lt.json';
import lvLocale from './locales/lv.json';
import nlLocale from './locales/nl.json';
import noLocale from './locales/no.json';
import plLocale from './locales/pl.json';
import ruLocale from './locales/ru.json';
import svLocale from './locales/sv.json';
import ukLocale from './locales/uk.json';
import zhCNLocale from './locales/zh-CN.json';

export const LANGUAGE_KEY = 'language';
const COMPANY_DEFAULT_LANGUAGE_KEY = 'company_language';
export const DEFAULT_LANG = 'en';

export const supportedLanguages = [
  {
    code: 'en',
    name: 'English',
  },
  {
    code: 'de',
    name: 'Deutsch',
  },
  {
    code: 'da',
    name: 'Dansk',
  },
  {
    code: 'no',
    name: 'Norsk',
  },
  {
    code: 'fi',
    name: 'Suomi',
  },
  {
    code: 'fm',
    name: 'Suomi (Neutraali)',
  },
  {
    code: 'sv',
    name: 'Svenska',
  },
  {
    code: 'ru',
    name: 'Русский',
  },
  {
    code: 'nl',
    name: 'Nederlands',
  },
  {
    code: 'pl',
    name: 'Polski',
  },
  {
    code: 'zh-CN',
    name: '中文',
  },
  {
    code: 'et',
    name: 'Eesti',
  },
  {
    code: 'uk',
    name: 'Українська',
  },
  {
    code: 'lv',
    name: 'Latviešu',
  },
  {
    code: 'lt',
    name: 'Lietuvių',
  },
  {
    code: 'it',
    name: 'Italiano',
  },
  {
    code: 'fr',
    name: 'Français',
  },
  {
    code: 'cs',
    name: 'Čeština',
  },
];

export const getCurrentLanguage = (fallbackLanguage?: string): string => {
  return getStored<string>(LANGUAGE_KEY) || fallbackLanguage || DEFAULT_LANG;
};

export const getCompanyDefaultLanguage = () => {
  return getStored(COMPANY_DEFAULT_LANGUAGE_KEY);
};

// Dates localization
export const getAdapterLocale = (languageCode?: string): string => {
  const language = languageCode ? languageCode : getCurrentLanguage('en-gb');

  const localeConversions = [
    {
      languages: ['en', 'en-us'],
      adapterLocale: 'en-gb',
    },
    {
      languages: ['fm'],
      adapterLocale: 'fi',
    },
  ];

  return (
    localeConversions.find((lc) => lc.languages.includes(language))
      ?.adapterLocale || language
  );
};

export const getMomentDateLibInstance = (languageCode?: string) => {
  moment.locale(getAdapterLocale(languageCode));
  return moment;
};

// Translations
type Translations = {
  [p: string]: string;
};

// Static translation maps
const localeMap: Record<string, Translations> = {
  en: enLocale,
  de: deLocale,
  da: daLocale,
  no: noLocale,
  fi: fiLocale,
  fm: fmLocale,
  sv: svLocale,
  ru: ruLocale,
  nl: nlLocale,
  pl: plLocale,
  'zh-CN': zhCNLocale,
  et: etLocale,
  uk: ukLocale,
  lv: lvLocale,
  lt: ltLocale,
  it: itLocale,
  fr: frLocale,
  cs: csLocale,
};

const legacyLocaleMap: Record<string, Translations> = {
  en: enLegacy,
  de: deLegacy,
  da: daLegacy,
  no: noLegacy,
  fi: fiLegacy,
  fm: fmLegacy,
  sv: svLegacy,
  ru: ruLegacy,
  nl: nlLegacy,
  pl: plLegacy,
  zh: zhLegacy,
  et: etLegacy,
  uk: ukLegacy,
  lv: lvLegacy,
  lt: ltLegacy,
  it: itLegacy,
  fr: frLegacy,
  cs: csLegacy,
};

const errorLocaleMap: Record<string, Translations> = {
  en: enError,
  de: deError,
  da: daError,
  no: noError,
  fi: fiError,
  fm: fmError,
  sv: svError,
  ru: ruError,
  nl: nlError,
  pl: plError,
  zh: zhError,
  et: etError,
  uk: ukError,
  lv: lvError,
  lt: ltError,
  it: itError,
  fr: frError,
  cs: csError,
};

const formioLocales: Record<string, Translations> = {
  cs: csFormio,
  da: daFormio,
  de: deFormio,
  en: enFormio,
  et: etFormio,
  fi: fiFormio,
  fm: fmFormio,
  fr: frFormio,
  it: itFormio,
  lt: ltFormio,
  lv: lvFormio,
  nl: nlFormio,
  no: noFormio,
  pl: plFormio,
  sv: svFormio,
};

export const getResources = (
  customOverrides?: Record<string, Translations>,
) => {
  const getShortLangCode = (langCode: string) => langCode.substring(0, 2);

  const getTranslations = (langCode: string) => {
    const filterEmptyTranslations = (translations: Translations) =>
      Object.fromEntries(
        Object.entries(translations).filter(([k, v]) => !(v === '')),
      );

    const mergeTranslationSets = (translations: Translations[]) =>
      Object.assign(
        {},
        ...translations.map((obj) => filterEmptyTranslations(obj)),
      );

    const shortLangCode = getShortLangCode(langCode);
    const translationSets = [
      localeMap[langCode] || {},
      legacyLocaleMap[shortLangCode] || {},
      errorLocaleMap[shortLangCode] || {},
    ];

    return mergeTranslationSets(translationSets);
  };

  const base = Object.assign(
    {},
    ...supportedLanguages.map((lc) => {
      return {
        [getShortLangCode(lc.code)]: {
          translation: getTranslations(lc.code),
        },
      };
    }),
  );

  if (customOverrides) {
    Object.entries(customOverrides).forEach(([lang, overrides]) => {
      if (!base[lang]) base[lang] = { translation: {} };
      base[lang].translation = {
        ...base[lang].translation,
        ...overrides,
      };
    });
  }

  return base;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: getResources(),
    lng: getCurrentLanguage('en'),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export const getLanguageLabel = (langCode: string): string => {
  if (langCode) {
    const result = supportedLanguages.find(
      (s: { code: string; name: string }) => s.code === langCode,
    );
    if (result) {
      return result.name;
    }
  }
  return '';
};

export const getFormioEmbeddedTranslations = (
  langCode: string,
): Translations => {
  return { ...(formioLocales[langCode] ?? {}) };
};
